import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Bell, BellOff, Info } from 'lucide-react';
import { toast } from 'sonner';

const NotificationSettings = ({ isOpen, onClose }) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [permission, setPermission] = useState('default');
  const [settings, setSettings] = useState({
    eventUpdates: true,
    paymentAlerts: true,
    rsvpNotifications: true,
    remindersBefore24h: true
  });

  useEffect(() => {
    // Check current notification permission
    if ('Notification' in window) {
      setPermission(Notification.permission);
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported on this device');
      return;
    }

    if (permission === 'granted') {
      toast.info('Notifications already enabled');
      return;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notifications enabled successfully!');
        
        // Show test notification
        new Notification('Contribo Notifications', {
          body: 'You will now receive updates about your events',
          icon: '/contribo-logo.png',
          badge: '/contribo-logo.png'
        });
      } else {
        toast.error('Notification permission denied');
      }
    } catch (error) {
      console.error('Notification permission error:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const handleToggleSetting = (key) => {
    if (!notificationsEnabled) {
      toast.error('Please enable notifications first');
      return;
    }
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success('Notification preference updated');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-900" />
            Notification Settings
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Main Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-semibold">Enable Notifications</Label>
              <p className="text-sm text-gray-500 mt-1">
                Get updates about your events and RSVPs
              </p>
            </div>
            <Switch
              checked={notificationsEnabled}
              onCheckedChange={requestNotificationPermission}
              disabled={permission === 'denied'}
            />
          </div>

          {permission === 'denied' && (
            <Alert className="bg-red-50 border-red-200">
              <Info className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-sm text-red-800">
                Notifications blocked. Please enable them in your browser/device settings.
              </AlertDescription>
            </Alert>
          )}

          {/* Notification Preferences */}
          {notificationsEnabled && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-sm text-gray-700">Notification Types</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="event-updates" className="text-sm">Event Updates</Label>
                  <Switch
                    id="event-updates"
                    checked={settings.eventUpdates}
                    onCheckedChange={() => handleToggleSetting('eventUpdates')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="payment-alerts" className="text-sm">Payment Alerts</Label>
                  <Switch
                    id="payment-alerts"
                    checked={settings.paymentAlerts}
                    onCheckedChange={() => handleToggleSetting('paymentAlerts')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="rsvp-notifications" className="text-sm">RSVP Notifications</Label>
                  <Switch
                    id="rsvp-notifications"
                    checked={settings.rsvpNotifications}
                    onCheckedChange={() => handleToggleSetting('rsvpNotifications')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="reminders" className="text-sm">Event Reminders (24h before)</Label>
                  <Switch
                    id="reminders"
                    checked={settings.remindersBefore24h}
                    onCheckedChange={() => handleToggleSetting('remindersBefore24h')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Info Box */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-xs text-blue-800">
              <strong>Note:</strong> Push notifications are currently in beta. Full integration with Firebase Cloud Messaging will be available soon.
            </AlertDescription>
          </Alert>
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose} className="bg-blue-900 hover:bg-blue-800">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationSettings;
